import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilter,
  faClipboardList,
  faSort,
  faSortUp,
  faSortDown,
  faArrowLeft,
  faEye,
} from "@fortawesome/free-solid-svg-icons";

import HeaderComponent from "../components/common/HeaderComponent";
import FooterComponent from "../components/common/FooterComponent";
import ErrorModalComponent from "../components/common/ErrorModalComponent";
import LoaderComponent from "../components/common/LoaderComponent";
import PaginationComponent from "../components/common/PaginationComponent";
import { useMembershipRequestsContext } from "../context/MembershipRequestsContext";
import apiClient from "../api/apiClient";

export default function MembershipRequestsPage() {
  const navigate = useNavigate();

  const {
    membershipRequests,
    setMembershipRequests,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    pageNumber,
    setPageNumber,
    totalPages,
    setTotalPages,
    totalElements,
    setTotalElements,
    hasNextPage,
    setHasNextPage,
    hasPreviousPage,
    setHasPreviousPage,
    dataLoaded,
    setDataLoaded,
  } = useMembershipRequestsContext();

  const pageSize = 10;
  const [shouldFetch, setShouldFetch] = useState(false);

  const [loader, setLoader] = useState({
    show: false,
    message: "",
  });

  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  const closeErrorModal = () => {
    setErrorModal({
      show: false,
      title: "",
      message: "",
    });
  };

  useEffect(() => {
    // Only fetch if data hasn't been loaded yet
    if (!dataLoaded) {
      fetchMembershipRequests();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Only fetch when user manually triggers it
    if (shouldFetch && dataLoaded) {
      fetchMembershipRequests();
      setShouldFetch(false);
    }
    // eslint-disable-next-line
  }, [shouldFetch]);

  const fetchMembershipRequests = async () => {
    try {
      setLoader({
        show: true,
        message: "Loading membership requests. Please wait...",
      });

      const filters = {};

      // Add status filter
      if (statusFilter !== "ALL") {
        filters.requestStatus = statusFilter;
      }

      // Add reference ID or Name filter if search is active
      if (searchQuery.trim()) {
        filters.referenceIdOrName = searchQuery.trim();
      }

      const requestBody = {
        requestHeader: {
          requestId: `REQ-${Date.now()}`,
          requestTimestamp: new Date().toISOString(),
        },
        requestData: {
          pageNumber: pageNumber,
          pageSize: pageSize,
          sortBy: sortBy,
          sortDirection: sortDirection,
          filters: filters,
        },
      };

      const response = await apiClient.post(
        "/v1/memberships/get-requests",
        requestBody
      );

      setMembershipRequests(response.data.responseData.membershipRequests);

      const metadata = response.data.responseMetadata;
      setTotalPages(metadata.totalPages);
      setTotalElements(metadata.totalElements);
      setHasNextPage(metadata.hasNextPage);
      setHasPreviousPage(metadata.hasPreviousPage);
      setDataLoaded(true);
    } catch (err) {
      setErrorModal({
        show: true,
        title: "Failed to Load Membership Requests",
        message:
          err.response?.data?.errorMessage ||
          "Unable to load membership requests. Please try again later.",
      });
    } finally {
      setLoader({
        show: false,
        message: "",
      });
    }
  };

  const handleSearch = () => {
    // Reset to first page and trigger search
    setPageNumber(0);
    setShouldFetch(true);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setPageNumber(0);
    setShouldFetch(true);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setPageNumber(0);
    setShouldFetch(true);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(column);
      setSortDirection("DESC");
    }
    setPageNumber(0);
    setShouldFetch(true);
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return faSort;
    }
    return sortDirection === "ASC" ? faSortUp : faSortDown;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      SUBMITTED: { bg: "bg-warning", text: "text-dark" },
      ACCEPTED: { bg: "bg-secondary", text: "text-white" },
      UNDER_REVIEW: { bg: "bg-info", text: "text-dark" },
      APPROVED: { bg: "bg-success", text: "text-white" },
      REJECTED: { bg: "bg-danger", text: "text-white" },
    };

    const config = statusConfig[status] || {
      bg: "bg-secondary",
      text: "text-white",
    };

    return (
      <span className={`badge ${config.bg} ${config.text}`}>
        {status.replace(/_/g, " ")}
      </span>
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const d = new Date(dateString);
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const handleViewRequest = (referenceId) => {
    const cleanReferenceId = referenceId.replace("#", "");
    navigate(`/exco-portal/membership-requests/${cleanReferenceId}`);
  };

  return (
    <div className="d-flex flex-column min-vh-100 overflow-x-hidden">
      <HeaderComponent user={null} />
      {/* <NavigationPanelComponent /> */}

      <main className="container my-5 flex-grow-1">
        <LoaderComponent
          show={loader.show}
          fullPage={true}
          message={loader.message}
        />
        <ErrorModalComponent
          show={errorModal.show}
          title={errorModal.title}
          message={errorModal.message}
          onClose={closeErrorModal}
        />
        {/* Back Button */}
        <div className="mb-4">
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              window.history.back();
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </button>
        </div>

        {/* Page Header */}
        <div className="mb-4">
          <h3 className="fw-bold mb-2">Membership Requests</h3>
          <p className="text-muted">
            Manage and review all membership registration requests
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="card rounded-3 border-vcg-muted mb-4">
          <div className="card-body p-4">
            <div className="row g-3">
              {/* Search by Reference ID */}
              <div className="col-lg-6">
                <label className="form-label fw-semibold">
                  <FontAwesomeIcon icon={faSearch} className="me-2" />
                  Search by Reference ID or Name
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Reference ID or Name"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button
                    className="btn btn-dark"
                    onClick={handleSearch}
                    disabled={!searchQuery.trim()}
                  >
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    Search
                  </button>
                </div>
              </div>

              {/* Status Filter */}
              <div className="col-lg-6">
                <label className="form-label fw-semibold">
                  <FontAwesomeIcon icon={faFilter} className="me-2" />
                  Filter by Status
                </label>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="REVIEW_STARTED">Under Review</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {(searchQuery || statusFilter !== "ALL") && (
          <div className="mb-3">
            <small className="text-muted">Active filters: </small>
            {searchQuery && (
              <span className="badge bg-primary me-2">
                Reference ID or Name: {searchQuery}
                <button
                  className="btn btn-sm p-0 ms-2 text-white border-0"
                  style={{ fontSize: "0.8rem" }}
                  onClick={handleClearSearch}
                >
                  ✕
                </button>
              </span>
            )}
            {statusFilter !== "ALL" && (
              <span className="badge bg-secondary me-2">
                Status: {statusFilter}
                <button
                  className="btn btn-sm p-0 ms-2 text-white border-0"
                  style={{ fontSize: "0.8rem" }}
                  onClick={() => handleStatusFilterChange("ALL")}
                >
                  ✕
                </button>
              </span>
            )}
          </div>
        )}

        {/* Results Info */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <small className="text-muted">
              Showing {membershipRequests.length} of {totalElements} requests
            </small>
          </div>
        </div>

        {/* Requests Table */}
        <div className="card rounded-3 border-vcg-muted p-4 mb-4">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("referenceId")}
                    >
                      Reference ID
                      <FontAwesomeIcon
                        icon={getSortIcon("referenceId")}
                        className="ms-2"
                        size="sm"
                      />
                    </th>
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("member.fullName")}
                    >
                      Applicant Name
                      <FontAwesomeIcon
                        icon={getSortIcon("member.fullName")}
                        className="ms-2"
                        size="sm"
                      />
                    </th>
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("requestStatus")}
                    >
                      Status
                      <FontAwesomeIcon
                        icon={getSortIcon("requestStatus")}
                        className="ms-2"
                        size="sm"
                      />
                    </th>
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("createdDateTime")}
                    >
                      Created Date
                      <FontAwesomeIcon
                        icon={getSortIcon("createdDateTime")}
                        className="ms-2"
                        size="sm"
                      />
                    </th>
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("updatedDateTime")}
                    >
                      Last Updated
                      <FontAwesomeIcon
                        icon={getSortIcon("updatedDateTime")}
                        className="ms-2"
                        size="sm"
                      />
                    </th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {membershipRequests.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5">
                        <div className="text-muted">
                          <FontAwesomeIcon
                            icon={faClipboardList}
                            size="3x"
                            className="mb-3 opacity-25"
                          />
                          <p className="mb-0">No membership requests found</p>
                          {(statusFilter !== "ALL" || searchQuery) && (
                            <small>Try adjusting your filters</small>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    membershipRequests.map((request, idx) => (
                      <tr key={idx}>
                        <td className="fw-semibold">{request.referenceId}</td>
                        <td>{request.fullName}</td>
                        <td>{getStatusBadge(request.currentStatus)}</td>
                        <td>{formatDateTime(request.createdDateTime)}</td>
                        <td>{formatDateTime(request.lastUpdatedDateTime)}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-dark btn-sm"
                            onClick={() =>
                              handleViewRequest(request.referenceId)
                            }
                          >
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationComponent
            pageNumber={pageNumber + 1}
            totalPages={totalPages}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onPageChange={(page) => {
              setPageNumber(page - 1);
              setShouldFetch(true);
            }}
          />
        )}
      </main>

      <FooterComponent />
    </div>
  );
}
